import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../layout/AppLayout';
import MobileHeader from '../layout/MobileHeader';
import { useAuth } from '../contexts/AuthContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€', USD: '$', GBP: '£', MXN: '$', BRL: 'R$',
  ARS: '$', CLP: '$', COP: '$', PEN: 'S/', CAD: '$', AUD: '$',
};
const getCurrencySymbol = (code?: string | null) =>
  code ? (CURRENCY_SYMBOLS[code] ?? code) : '€';

// ─── Sub-components ───────────────────────────────────────────────────────────

const Card: React.FC<{ title?: string; children: React.ReactNode; className?: string; padding?: string }> = ({ title, children, className = '', padding = 'p-4' }) => (
  <div className={`bg-white border border-gray-200 rounded-xl ${padding} ${className}`}>
    {title && <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-3">{title}</p>}
    {children}
  </div>
);

interface SliderRowProps {
  label: string;
  id: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
  display: string;
}

const SliderRow: React.FC<SliderRowProps> = ({ label, min, max, step, value, onChange, display }) => (
  <div className="flex items-center gap-3 mb-2">
    <label className="text-[11px] font-medium text-gray-600 w-40 flex-shrink-0 leading-tight">{label}</label>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="flex-1 h-1.5 appearance-none bg-gray-200 rounded-full outline-none cursor-pointer accent-[#1E0DB8]"
    />
    <span className="text-[11px] font-semibold text-gray-900 text-right w-16 flex-shrink-0">{display}</span>
  </div>
);

type PricingMode = 'per-quote' | 'pct-order';

interface PricingModeToggleProps {
  mode: PricingMode;
  onChange: (m: PricingMode) => void;
  labelPerQuote: string;
  labelPctOrder: string;
}

const PricingModeToggle: React.FC<PricingModeToggleProps> = ({ mode, onChange, labelPerQuote, labelPctOrder }) => (
  <div className="flex w-full rounded-lg border border-gray-200 overflow-hidden mb-3">
    {(['per-quote', 'pct-order'] as PricingMode[]).map((m) => {
      const active = mode === m;
      return (
        <button
          key={m}
          onClick={() => onChange(m)}
          className={`flex-1 text-[11px] font-semibold py-1.5 transition-all duration-150 ${
            active
              ? 'bg-[#EEF0FD] text-[#1E0DB8]'
              : 'bg-white text-gray-400 hover:text-gray-600'
          }`}
        >
          {m === 'per-quote' ? labelPerQuote : labelPctOrder}
        </button>
      );
    })}
  </div>
);

interface MetricCardProps {
  label: string;
  value: string;
  accent: 'blue' | 'neutral' | 'amber' | 'green';
}

const accentClasses: Record<MetricCardProps['accent'], string> = {
  blue:    'text-[#1E0DB8]',
  neutral: 'text-gray-800',
  amber:   'text-amber-600',
  green:   'text-[#137F67]',
};

const MetricCard: React.FC<MetricCardProps> = ({ label, value, accent }) => (
  <div className="bg-gray-50 rounded-xl p-3 flex flex-col gap-1">
    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest leading-tight">{label}</p>
    <p className={`font-inter-tight text-xl font-semibold leading-none ${accentClasses[accent]}`}>{value}</p>
  </div>
);

interface BreakdownRowProps {
  left: string;
  right: string;
  bold?: boolean;
  blue?: boolean;
  green?: boolean;
  large?: boolean;
  borderBottom?: boolean;
}

const BreakdownRow: React.FC<BreakdownRowProps> = ({ left, right, bold, blue, green, large, borderBottom = true }) => (
  <div className={`flex justify-between items-center py-2 ${borderBottom ? 'border-b border-gray-100' : ''}`}>
    <span className={`text-[11px] leading-tight ${bold ? 'font-bold text-gray-900' : 'text-gray-500'}`}>{left}</span>
    <span className={`font-inter-tight leading-none flex-shrink-0 ml-4 ${large ? 'text-[1.25rem] font-bold' : 'text-[12px] font-semibold'} ${blue ? 'text-[#1E0DB8]' : green ? 'text-[#137F67]' : 'text-gray-700'}`}>{right}</span>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const ROIB2CCalculatorPage: React.FC = () => {
  const navigate   = useNavigate();
  const { t }      = useTranslation();
  const { currentUser } = useAuth();
  const sym        = getCurrencySymbol(currentUser?.roi_currency);

  // ── Pricing mode ──
  const [pricingMode, setPricingMode] = useState<PricingMode>(
    (currentUser?.roi_b2c_pricing_mode as PricingMode | null | undefined) ?? 'per-quote'
  );

  // ── Inputs — both cpq and pct always kept in state ──
  const [inputs, setInputs] = useState({
    quotes: currentUser?.roi_b2c_quotes ?? 10000,
    cr:     currentUser?.roi_b2c_cr     ?? 2.5,
    aov:    currentUser?.roi_b2c_aov    ?? 300,
    gm:     currentUser?.roi_b2c_gm     ?? 60,
    cpq:    currentUser?.roi_b2c_cpq    ?? 0.50,
    pct:    currentUser?.roi_b2c_pct    ?? 3.0,
  });

  const set = (key: keyof typeof inputs) => (v: number) =>
    setInputs((prev) => ({ ...prev, [key]: v }));

  // ── Derived calculations ──
  const orders       = inputs.quotes * (inputs.cr / 100);
  const grossRev     = inputs.aov * (inputs.gm / 100);
  const costPerOrder = pricingMode === 'per-quote'
    ? inputs.cpq / (inputs.cr / 100)
    : inputs.aov * (inputs.pct / 100);
  const netRev       = grossRev - costPerOrder;
  const roi          = costPerOrder > 0 ? netRev / costPerOrder : 0;

  // ── Display formatters ──
  const fmtOrders       = Math.round(orders).toLocaleString();
  const fmtGrossRev     = sym + grossRev.toFixed(2);
  const fmtCostPerOrder = sym + costPerOrder.toFixed(2);
  const fmtNetRev       = sym + netRev.toFixed(2);
  const fmtRoi          = roi.toFixed(1) + 'x';
  const fmtCr           = inputs.cr.toFixed(1) + '%';
  const fmtGm           = inputs.gm.toFixed(1) + '%';
  const fmtCpq          = sym + inputs.cpq.toFixed(2);
  const fmtPct          = inputs.pct.toFixed(1) + '%';
  const fmtAov          = sym + Math.round(inputs.aov).toLocaleString();
  const fmtQuotes       = Math.round(inputs.quotes).toLocaleString();

  // Breakdown row 4 — changes with pricing mode
  const row4Label = pricingMode === 'per-quote'
    ? t('roiB2cCalculator.breakdown.costOfQuoteCr')
    : t('roiB2cCalculator.breakdown.cataFeeTimesOrder');
  const row4Value = pricingMode === 'per-quote'
    ? `${fmtCpq} / ${fmtCr}`
    : `${fmtPct} × ${fmtAov}`;

  return (
    <AppLayout pageTitle={t('roiB2cCalculator.pageTitle')} pageSubtitle={t('roiB2cCalculator.pageSubtitle')}>
      <MobileHeader title={t('roiB2cCalculator.mobileTitle')} showBack={false} onBackClick={() => navigate('/')} />

      <div className="flex-1 overflow-hidden bg-main-bg flex flex-col">

        {/* ── B2B / B2C mode switcher (only shown when B2B is also enabled) ── */}
        {currentUser?.roi_b2b_enabled && (
          <div className="flex items-center gap-2 px-4 pt-3 pb-0 flex-shrink-0">
            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{t('roiB2cCalculator.calculatorMode')}</span>
            <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5 border border-gray-200">
              <button
                onClick={() => navigate('/roi-calculator/b2b')}
                className="text-[11px] font-medium px-3 py-1 rounded-md text-gray-500 hover:text-gray-800 transition-all duration-150"
              >
                {t('roiB2cCalculator.b2bLabel')}
              </button>
              <button className="text-[11px] font-bold px-3 py-1 rounded-md bg-white text-[#1E0DB8] shadow-sm">
                {t('roiB2cCalculator.b2cLabel')}
              </button>
            </div>
          </div>
        )}

        {/* ── Single centered content area ── */}
        <div className="flex-1 overflow-y-auto min-h-0 px-4 py-3">
          <div className="w-full max-w-3xl mx-auto flex flex-col gap-3">

            {/* ── Inner two-column grid ── */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-3">

              {/* ══ COL 1 — Input Panel ══ */}
              <div className="flex flex-col gap-3">
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{t('roiB2cCalculator.sections.customerAssumptions')}</p>

                <Card padding="p-3">
                  <SliderRow
                    label={t('roiB2cCalculator.inputs.quotesPerMonth')}
                    id="quotes"
                    min={500} max={50000} step={500}
                    value={inputs.quotes}
                    onChange={set('quotes')}
                    display={fmtQuotes}
                  />
                  <SliderRow
                    label={t('roiB2cCalculator.inputs.conversionRate')}
                    id="cr"
                    min={0.5} max={15} step={0.1}
                    value={inputs.cr}
                    onChange={set('cr')}
                    display={fmtCr}
                  />
                  <SliderRow
                    label={t('roiB2cCalculator.inputs.orderValue', { sym })}
                    id="aov"
                    min={50} max={2000} step={10}
                    value={inputs.aov}
                    onChange={set('aov')}
                    display={fmtAov}
                  />
                  <SliderRow
                    label={t('roiB2cCalculator.inputs.grossMargin')}
                    id="gm"
                    min={20} max={80} step={1}
                    value={inputs.gm}
                    onChange={set('gm')}
                    display={fmtGm}
                  />

                  {/* Divider + CATA pricing section */}
                  <div className="border-t border-gray-100 my-3" />
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-2">{t('roiB2cCalculator.sections.cataPrice')}</p>

                  {/* Pricing mode toggle */}
                  <PricingModeToggle
                    mode={pricingMode}
                    onChange={setPricingMode}
                    labelPerQuote={t('roiB2cCalculator.pricingMode.perQuote', { sym })}
                    labelPctOrder={t('roiB2cCalculator.pricingMode.pctOrder')}
                  />

                  {/* Conditional slider */}
                  {pricingMode === 'per-quote' ? (
                    <SliderRow
                      label={t('roiB2cCalculator.inputs.costPerQuote', { sym })}
                      id="cpq"
                      min={0.10} max={2.00} step={0.05}
                      value={inputs.cpq}
                      onChange={set('cpq')}
                      display={fmtCpq}
                    />
                  ) : (
                    <SliderRow
                      label={t('roiB2cCalculator.inputs.cataFeePercent')}
                      id="pct"
                      min={0.5} max={10} step={0.1}
                      value={inputs.pct}
                      onChange={set('pct')}
                      display={fmtPct}
                    />
                  )}
                </Card>

                {/* ── Per-order economics breakdown ── */}
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{t('roiB2cCalculator.sections.perOrderEconomics')}</p>
                <Card padding="p-3">
                  <BreakdownRow left={t('roiB2cCalculator.breakdown.orderValue')}           right={fmtAov} />
                  <BreakdownRow left={t('roiB2cCalculator.breakdown.grossMargin')}          right={fmtGm} />
                  <BreakdownRow left={t('roiB2cCalculator.breakdown.grossRevenuePerOrder')} right={fmtGrossRev} />
                  <BreakdownRow left={row4Label}                                            right={row4Value} />
                  <BreakdownRow left={t('roiB2cCalculator.breakdown.cataCostPerOrder')}     right={fmtCostPerOrder} borderBottom={false} />

                  <hr className="border-t border-gray-200 my-2" />

                  <BreakdownRow left={t('roiB2cCalculator.breakdown.netRevenuePerOrder')} right={fmtNetRev} bold blue borderBottom={false} />
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-[12px] font-bold text-gray-900">{t('roiB2cCalculator.breakdown.roiMultiple')}</span>
                    <span className={`font-inter-tight font-bold text-[1.25rem] leading-none ${roi >= 0 ? 'text-[#137F67]' : 'text-red-500'}`}>
                      {fmtRoi}
                    </span>
                  </div>
                </Card>
              </div>

              {/* ══ COL 2 — Results ══ */}
              <div className="flex flex-col gap-2">
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{t('roiB2cCalculator.sections.results')}</p>

                {/* 2×2 Metric cards */}
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard label={t('roiB2cCalculator.metrics.monthlyOrders')}       value={fmtOrders}       accent="blue" />
                  <MetricCard label={t('roiB2cCalculator.metrics.grossRevenueOrder')}   value={fmtGrossRev}     accent="neutral" />
                  <MetricCard label={t('roiB2cCalculator.metrics.cataCostOrder')}       value={fmtCostPerOrder} accent="amber" />
                  <MetricCard label={t('roiB2cCalculator.metrics.roiMultiple')}         value={fmtRoi}          accent="green" />
                </div>

                {/* ROI highlight card */}
                <div className="bg-[#1E0DB8] rounded-xl px-4 py-3 flex flex-col gap-1.5">
                  <p className="text-[9px] font-bold text-[#DBD8FF] uppercase tracking-wider">{t('roiB2cCalculator.highlight.roiMultiple')}</p>
                  <p className="text-[9px] text-[#DBD8FF]/60">{t('roiB2cCalculator.highlight.formula')}</p>
                  <div className="flex items-end justify-between mt-1">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[9px] text-[#DBD8FF]">{t('roiB2cCalculator.highlight.netRevOrder')}</span>
                      <span className="font-inter-tight text-base font-semibold text-white leading-none">{fmtNetRev}</span>
                    </div>
                    <p className={`font-inter-tight text-5xl font-medium leading-none flex-shrink-0 ${roi >= 0 ? 'text-white' : 'text-red-300'}`}>
                      {fmtRoi}
                    </p>
                  </div>
                </div>

                {/* Cost summary */}
                <div className="grid grid-cols-2 gap-2">
                  <Card>
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">{t('roiB2cCalculator.costCard.cataCostOrder')}</p>
                    <p className="font-inter-tight text-lg font-medium text-amber-600 leading-none">{fmtCostPerOrder}</p>
                    <p className="text-[9px] text-gray-400 mt-1">
                      {pricingMode === 'per-quote'
                        ? <>{fmtCpq} ÷ {fmtCr} {t('roiB2cCalculator.costCard.crSuffix')}</>
                        : <>{fmtPct} × {fmtAov}</>
                      }
                    </p>
                  </Card>
                  <Card>
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">{t('roiB2cCalculator.costCard.grossRevOrder')}</p>
                    <p className="font-inter-tight text-lg font-medium text-[#137F67] leading-none">{fmtGrossRev}</p>
                    <p className="text-[9px] text-gray-400 mt-1">{fmtAov} × {fmtGm} {t('roiB2cCalculator.costCard.marginSuffix')}</p>
                  </Card>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default ROIB2CCalculatorPage;
